import React, { useMemo, useState } from 'react'
import { Radio, Typography } from '@surecompanies/core_components'
import { Line } from 'react-chartjs-2'
import dayjs from 'dayjs'
import type { CarrierMonthHistoryItemDto } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import './chartjsSetup'
import styles from './CarrierDetail.module.scss'

const { Text, Title } = Typography

type ViewMode = 'count' | 'pct'

interface Props {
  monthHistory: CarrierMonthHistoryItemDto[]
  carrierName?: string
}

export function CarrierMonthTrendChart({ monthHistory, carrierName }: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('count')

  // API returns month history newest-first; reverse for the chronological trend
  // line and derive a display label from the YYYY-MM period.
  const monthHistoryChrono = useMemo(
    () =>
      [...monthHistory].reverse().map((m) => ({
        ...m,
        monthLabel: dayjs(`${m.period}-01`).format('MMM YYYY'),
      })),
    [monthHistory]
  )

  const monthTrendData = useMemo(() => {
    const labels = monthHistoryChrono.map((m) => m.monthLabel)
    if (viewMode === 'pct') {
      return {
        labels,
        datasets: [
          {
            label: 'Success Rate %',
            data: monthHistoryChrono.map((m) => m.successRate),
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            yAxisID: 'y',
            fill: false,
            tension: 0.3,
          },
          {
            label: 'Failed Rate %',
            data: monthHistoryChrono.map((m) => m.failRate),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            yAxisID: 'y',
            fill: false,
            tension: 0.3,
          },
        ],
      }
    }
    return {
      labels,
      datasets: [
        {
          label: 'Total Benefits',
          data: monthHistoryChrono.map((m) => m.benefits),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          yAxisID: 'yCount',
          fill: false,
          tension: 0.3,
        },
        {
          label: 'Success',
          data: monthHistoryChrono.map((m) => m.completed),
          borderColor: 'rgb(5, 150, 105)',
          backgroundColor: 'rgba(5, 150, 105, 0.1)',
          yAxisID: 'yCount',
          fill: false,
          tension: 0.3,
        },
        {
          label: 'Failed',
          data: monthHistoryChrono.map((m) => m.failed),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          yAxisID: 'yCount',
          fill: false,
          tension: 0.3,
        },
      ],
    }
  }, [monthHistoryChrono, viewMode])

  const monthTrendOptions = useMemo(() => {
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: true, position: 'top' as const },
      },
    }
    if (viewMode === 'pct') {
      return {
        ...base,
        scales: {
          y: {
            type: 'linear' as const,
            position: 'left' as const,
            min: 0,
            max: 100,
            title: { display: true, text: 'Rate (%)' },
            ticks: {
              callback: (value: number | string) =>
                `${Number(value).toFixed(0)}%`,
            },
          },
        },
      }
    }
    return {
      ...base,
      scales: {
        yCount: {
          type: 'linear' as const,
          position: 'left' as const,
          min: 0,
          title: { display: true, text: 'Cases' },
          ticks: { precision: 0 },
        },
      },
    }
  }, [viewMode])

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title level={5} className={styles.sectionTitle}>
          Month-over-month — {carrierName}
        </Title>
        <Radio.Group
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value as ViewMode)}
          optionType="button"
          size="small"
          options={[
            { label: 'Benefits', value: 'count' },
            { label: 'Trend', value: 'pct' },
          ]}
        />
      </div>
      <Text type="secondary" className={styles.hint}>
        {viewMode === 'pct'
          ? 'Success % and Failed % as a share of total benefits — a rising success rate (or falling failed rate) means the bot is improving independent of volume.'
          : 'Raw case volume per month — total benefits, successful, and failed. Switch to Trend for the success/failed rate.'}
      </Text>
      <div className={styles.monthTrendChartWrap}>
        <Line data={monthTrendData} options={monthTrendOptions} />
      </div>
    </div>
  )
}
